import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, Eye, Plus, Send, Trash2 } from 'lucide-react';
import {
  Overlay, Modal, ModalTitle, ModalField, ModalList, ModalActions,
  Select, Input, Message, Button, eventLabel,
} from './ui';

const ROLES = ['SPEAKER', 'MODERATOR', 'PANELIST'];
const blankRow = () => ({ role: 'SPEAKER', sessionTitle: '', sessionStartTime: '', sessionEndTime: '', talkTitle: '' });

// Reused by the list view (quick re-invite of a known speaker) and the
// detail view (approve & invite after reviewing a researched profile).
// `hasAccount` only changes the confirmation copy - the backend already
// reuses the existing account/profile either way, no AI research is
// triggered by sending an invite.
//
// One invite can grant several roles/sessions at once (a person moderating
// one session and speaking in another - see docs/speaker-session-roles.md),
// so this is a repeatable row editor rather than a single talk-title field.
// prefillAssignments comes from SpeakerProfile.detectedAssignments (program
// detection); the admin can freely edit, add, or remove rows before sending.
//
// Detected sessions only ever came from one specific event's program - they
// don't mean anything paired with a *different* event. So they're only ever
// live in the row editor while paired with the event they're actually for -
// tracked here as appliedForEventId, not just a boolean, so a change away
// from that event (not just away from prefillEventId) correctly clears them.
//
// Getting there has two paths: prefillEventId (the admin already confirmed
// this in bulk - see confirmDetectedEvent/mergeDetectedAssignments) fills
// both the dropdown and the rows immediately. Otherwise, if this profile has
// detectedAssignments sitting unconfirmed, picking *any* event here offers a
// one-click "use these sessions for it" - the bulk banner that confirms
// events is transient (gone once dismissed or the page reloads), so a
// profile opened later needs a way to do this that doesn't depend on it
// still being open.
//
// Sending is always preceded by a preview: onPreview(eventId, assignments)
// resolves to the exact email that would go out ({ subject, html }) without
// sending or saving anything, and the admin has to look at it before the
// "Send invitation" button even exists. The preview endpoint refuses what the
// send would refuse (e.g. already invited), so those errors surface here, in
// the dialog, before anything happens.
const InviteModal = ({ open, onClose, onConfirm, onPreview, busy, events, email, hasAccount, prefillAssignments, prefillEventId }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const roleLabel = role => t(`speakerMgmt.confirm.role.${role}`, role.charAt(0) + role.slice(1).toLowerCase());
  const [eventId, setEventId] = useState('');
  const [rows, setRows] = useState([blankRow()]);
  const [appliedForEventId, setAppliedForEventId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const mapAssignments = () => prefillAssignments.map(a => ({
    role: ROLES.includes(a.role) ? a.role : 'SPEAKER',
    sessionTitle: a.sessionTitle || '', sessionStartTime: a.sessionStartTime || '',
    sessionEndTime: a.sessionEndTime || '', talkTitle: a.talkTitle || '',
  }));

  useEffect(() => {
    if (open) {
      setEventId(prefillEventId ? String(prefillEventId) : '');
      setRows(prefillEventId && prefillAssignments?.length ? mapAssignments() : [blankRow()]);
      setAppliedForEventId(prefillEventId ? String(prefillEventId) : null);
      setPreview(null);
      setPreviewError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillAssignments, prefillEventId]);

  if (!open) return null;

  const updateRow = (index, patch) => setRows(current => current.map((row, i) => i === index ? { ...row, ...patch } : row));
  const addRow = () => setRows(current => [...current, blankRow()]);
  const removeRow = index => setRows(current => current.filter((_, i) => i !== index));
  const changeEvent = value => {
    // The current rows only mean anything paired with appliedForEventId -
    // leaving it (in either direction) clears them rather than silently
    // attaching detected data to a different event.
    if (appliedForEventId && value !== appliedForEventId) {
      setRows([blankRow()]);
      setAppliedForEventId(null);
    }
    setEventId(value);
  };
  const applyDetectedAssignments = () => {
    setRows(mapAssignments());
    setAppliedForEventId(eventId);
  };
  // Only offer this while there's an actual event picked and the rows are
  // still untouched - once the admin has started editing, silently
  // overwriting that would be worse than not offering the shortcut at all.
  const canApplyDetected = !appliedForEventId && !!eventId && prefillAssignments?.length > 0
    && rows.length === 1 && !rows[0].sessionTitle && !rows[0].talkTitle;

  const canPreview = !busy && !previewing && !!eventId && !!email && rows.length > 0;
  const assignments = () => rows.map(r => ({
    role: r.role, sessionTitle: r.sessionTitle.trim() || null, sessionStartTime: r.sessionStartTime.trim() || null,
    sessionEndTime: r.sessionEndTime.trim() || null, talkTitle: r.role === 'SPEAKER' ? (r.talkTitle.trim() || null) : null,
  }));
  const send = () => onConfirm(eventId, assignments());
  const showPreview = async () => {
    setPreviewing(true);
    setPreviewError('');
    try {
      setPreview(await onPreview(eventId, assignments()));
    } catch (err) {
      setPreviewError(typeof err.response?.data === 'string' && err.response.data
        ? err.response.data
        : t('speakerMgmt.preview.error', 'Could not prepare the preview.'));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Overlay onClick={() => !busy && onClose()}>
      <Modal onClick={e => e.stopPropagation()}
        style={{ maxWidth: preview ? '44rem' : '38rem', maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}>
        {preview ? (
          <>
            <ModalTitle><Eye size={18} color="#168fc5" /><FormattedMessage id="speakerMgmt.preview.title" defaultMessage="Preview of the invitation" /></ModalTitle>
            <div style={{ fontSize: '.82rem', color: '#475569', marginBottom: '.6rem', lineHeight: 1.5 }}>
              <div><strong><FormattedMessage id="speakerMgmt.preview.to" defaultMessage="To" />:</strong> {preview.to || email}</div>
              <div><strong><FormattedMessage id="speakerMgmt.preview.subject" defaultMessage="Subject" />:</strong> {preview.subject}</div>
              <div style={{ color: '#64748b', marginTop: '.2rem' }}>
                <FormattedMessage id="speakerMgmt.preview.hint" defaultMessage="This is exactly what will be sent. Links are switched off in this preview." />
              </div>
            </div>
            {/* Sandboxed: no scripts, and every link is pointed at a new tab
                the sandbox refuses to open, so nothing in the preview can be
                clicked through (the invite link is a dummy anyway). */}
            <iframe title={t('speakerMgmt.preview.title', 'Preview of the invitation')} sandbox="" srcDoc={`<base target="_blank">${preview.html}`}
              style={{ flex: '1 1 auto', width: '100%', minHeight: '55vh', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
            <ModalActions>
              <Button secondary disabled={busy} onClick={() => setPreview(null)}><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.preview.back" defaultMessage="Back to edit" /></Button>
              <Button disabled={busy || !email} onClick={send}><Send size={16} /><FormattedMessage id="speakerMgmt.confirm.send" defaultMessage="Send invitation" /></Button>
            </ModalActions>
          </>
        ) : (
          <>
        <ModalTitle><AlertTriangle size={18} color="#b45309" /><FormattedMessage id="speakerMgmt.confirm.title" defaultMessage="Send speaker invitation?" /></ModalTitle>
        {/* The row editor grows unbounded when one person holds several
            roles/sessions in the same event - cap the modal and scroll the
            middle so Cancel/Send never leave the viewport. */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          <ModalField>
          <FormattedMessage id="speakerMgmt.confirm.eventLabel" defaultMessage="Which event is this person invited to?" />
          <Select value={eventId} onChange={e => changeEvent(e.target.value)}>
            <option value="">{t('speakerMgmt.confirm.selectEvent', 'Select an event…')}</option>
            {events.map(event => <option key={event.id} value={event.id}>{eventLabel(event, intl)}</option>)}
          </Select>
        </ModalField>
        {!appliedForEventId && prefillAssignments?.length > 0 && (
          <Message warn>
            <p style={{ margin: 0 }}>
              <FormattedMessage id="speakerMgmt.confirm.eventNotConfirmed"
                defaultMessage="{count, plural, one {A session was} other {# sessions were}} detected for this person from a program document, but no event was confirmed yet:"
                values={{ count: prefillAssignments.length }} />
            </p>
            <ul style={{ margin: '.4rem 0 .6rem', paddingLeft: '1.1rem' }}>
              {prefillAssignments.map((a, i) => (
                <li key={i}>
                  <strong>{roleLabel(ROLES.includes(a.role) ? a.role : 'SPEAKER')}</strong>
                  {a.sessionTitle ? <> — {a.sessionTitle}</> : ''}
                  {(a.sessionStartTime || a.sessionEndTime) ? <> ({[a.sessionStartTime, a.sessionEndTime].filter(Boolean).join('–')})</> : ''}
                  {a.talkTitle ? <> · {a.talkTitle}</> : ''}
                </li>
              ))}
            </ul>
            {eventId ? (
              <Button secondary type="button" onClick={applyDetectedAssignments} disabled={!canApplyDetected}>
                <FormattedMessage id="speakerMgmt.confirm.useDetected" defaultMessage="Use {count, plural, one {this} other {these #}} for this event" values={{ count: prefillAssignments.length }} />
              </Button>
            ) : (
              <span style={{ fontSize: '.78rem' }}>
                <FormattedMessage id="speakerMgmt.confirm.eventNotConfirmedHint" defaultMessage="Pick the event above, then use them here." />
              </span>
            )}
          </Message>
        )}

        <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#334155', marginBottom: '.35rem' }}>
          <FormattedMessage id="speakerMgmt.confirm.roles" defaultMessage="Role(s) and session(s)" />
        </div>
        <span style={{ fontWeight: 400, fontSize: '.75rem', color: '#64748b' }}>
          <FormattedMessage id="speakerMgmt.confirm.rolesHint" defaultMessage="One person can hold several roles across several sessions of the same event - add a row for each. Session title is optional for a plain single-track invite." />
        </span>
        {rows.map((row, index) => (
          <div key={index} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '.7rem', margin: '.6rem 0', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Select style={{ flex: '0 0 10rem' }} value={row.role} onChange={e => updateRow(index, { role: e.target.value })}>
                {ROLES.map(role => <option key={role} value={role}>{roleLabel(role)}</option>)}
              </Select>
              <Input style={{ flex: 1, minWidth: '10rem' }} placeholder={t('speakerMgmt.confirm.sessionTitle', 'Session title (optional)')}
                value={row.sessionTitle} onChange={e => updateRow(index, { sessionTitle: e.target.value })} />
              {rows.length > 1 && (
                <Button secondary type="button" onClick={() => removeRow(index)} title={t('speakerMgmt.confirm.removeRow', 'Remove this row')}>
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <Input style={{ flex: '0 0 7rem' }} placeholder={t('speakerMgmt.confirm.sessionStart', 'Start (e.g. 15:00)')}
                value={row.sessionStartTime} onChange={e => updateRow(index, { sessionStartTime: e.target.value })} />
              <Input style={{ flex: '0 0 7rem' }} placeholder={t('speakerMgmt.confirm.sessionEnd', 'End (e.g. 15:30)')}
                value={row.sessionEndTime} onChange={e => updateRow(index, { sessionEndTime: e.target.value })} />
              {row.role === 'SPEAKER' && (
                <Input style={{ flex: 1, minWidth: '10rem' }} placeholder={t('speakerMgmt.confirm.talkTitle', 'Talk title')}
                  value={row.talkTitle} onChange={e => updateRow(index, { talkTitle: e.target.value })} />
              )}
            </div>
          </div>
        ))}
        <Button secondary type="button" onClick={addRow} style={{ marginBottom: '1rem' }}>
          <Plus size={14} /><FormattedMessage id="speakerMgmt.confirm.addRow" defaultMessage="Add another role/session" />
        </Button>

        <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#334155' }}>
          <FormattedMessage id="speakerMgmt.confirm.intro" defaultMessage="This will:" />
        </div>
        <ModalList>
          <li><FormattedMessage id="speakerMgmt.confirm.point.email" defaultMessage="Send one combined invitation email to {email}" values={{ email: <strong>{email}</strong> }} /></li>
          {hasAccount
            ? <li><FormattedMessage id="speakerMgmt.confirm.point.reinvite" defaultMessage="Add this event to their existing speaker account (no AI research is repeated)" /></li>
            : <li><FormattedMessage id="speakerMgmt.confirm.point.account" defaultMessage="Create or activate a speaker account for this person" /></li>}
          <li><FormattedMessage id="speakerMgmt.confirm.point.results" defaultMessage="Show them the profile values you approved (the AI sources are never shown to the speaker)" /></li>
        </ModalList>
        {!email && <Message error><FormattedMessage id="speakerMgmt.confirm.noEmail" defaultMessage="No email yet — add one in the review form (or retry research) before you can send." /></Message>}
        {previewError && <Message error>{previewError}</Message>}
        </div>
        <ModalActions>
          <Button secondary disabled={busy} onClick={onClose}><FormattedMessage id="speakerMgmt.confirm.cancel" defaultMessage="Cancel" /></Button>
          <Button disabled={!canPreview} onClick={showPreview}><Eye size={16} />
            {previewing
              ? <FormattedMessage id="speakerMgmt.preview.loading" defaultMessage="Preparing preview…" />
              : <FormattedMessage id="speakerMgmt.preview.button" defaultMessage="Preview invitation" />}
          </Button>
        </ModalActions>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

export default InviteModal;
